import { apiSlice } from '../apiSlice';
import { Paginated, SuccessResponse } from '../DTO/common.interface';
import {
	EditEducation,
	FamilyFilter,
	FamilyMemberApplicationDTO,
	FamilyMemberDTO,
	FamilyMemberDuplicationCheckDTO,
	FamilyMemberDuplicationCheckRequest,
	FamilyMemberFileNoHistoryDTO,
	FamilyMemberRelationshipMergeSaveDTO,
	FamilyPageDTO,
	FamilyRequest,
	RelationshipSaveDTO,
	UpdatePersonal,
	UpdateServiceDetails,
} from '../DTO/family/family.interface';
import { FamilyMemberMemberDuplicationCheckDTO } from '../DTO/member-management/memberDuplication';
import { FamilyMemberRelationshipDTO } from '../DTO/member-management/admissionRequest';

export const familyApi = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getFamilyList: builder.query<
			SuccessResponse<Paginated<FamilyPageDTO>>,
			FamilyFilter
		>({
			query: (body) => ({
				url: `/web/family-member/page`,
				method: 'POST',
				body,
			}),
		}),
		// Returns the new family member, same shape as GET /web/family-member/{id}
		createFamily: builder.mutation<
			SuccessResponse<FamilyMemberDTO>,
			FamilyRequest
		>({
			query: (body) => ({
				url: `/web/family-member`,
				method: 'POST',
				body,
			}),
		}),
		// CHA-003: in-progress family members of this centre that may be the same person.
		checkFamilyMemberDuplication: builder.mutation<
			SuccessResponse<FamilyMemberDuplicationCheckDTO[]>,
			FamilyMemberDuplicationCheckRequest
		>({
			query: (body) => ({
				url: `/web/family-member/check-duplication`,
				method: 'POST',
				body,
			}),
		}),
		// CHA-004: closed (All services closed) family members of this centre that may be the same person.
		checkClosedFamilyMemberDuplication: builder.mutation<
			SuccessResponse<FamilyMemberDuplicationCheckDTO[]>,
			FamilyMemberDuplicationCheckRequest
		>({
			query: (body) => ({
				url: `/web/family-member/check-closed-duplication`,
				method: 'POST',
				body,
			}),
		}),
		// CHA-005 §2.1: admitted members of this centre that may be the same person.
		checkFamilyMemberMemberDuplication: builder.mutation<
			SuccessResponse<FamilyMemberMemberDuplicationCheckDTO[]>,
			FamilyMemberDuplicationCheckRequest
		>({
			query: (body) => ({
				url: `/web/family-member/check-member-duplication`,
				method: 'POST',
				body,
			}),
		}),
		// CHA-004 §七: every record in the chain (including this one), newest first.
		getFamilyMemberFileNoHistory: builder.query<
			SuccessResponse<FamilyMemberFileNoHistoryDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/family-member/${id}/file-no-history`,
			}),
		}),
		getFamilyNextFileNo: builder.query<
			SuccessResponse<any>,
			{ fiscalYear: string }
		>({
			query: (body) => ({
				url: `/web/family-member/generator-family-member-file-no`,
				method: 'POST',
				body,
			}),
		}),
		getFamilyToCreate: builder.query<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/family-member/to-create/${id}`,
				method: 'GET',
			}),
		}),
		getFamilyGroupList: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/family-member/${id}/family-member-group`,
				method: 'GET',
			}),
		}),
		getFamilyDetail: builder.query<SuccessResponse<FamilyMemberDTO>, string>({
			query: (id) => ({
				url: `/web/family-member/${id}`,
				method: 'GET',
			}),
			providesTags: ['FamilyDetail'],
		}),
		updateServiceDetails: builder.mutation<
			SuccessResponse<any>,
			UpdateServiceDetails
		>({
			query: (body) => ({
				url: `/web/family-member/${body.id}/service-details`,
				method: 'PUT',
				body,
			}),
		}),
		updatePersonal: builder.mutation<SuccessResponse<any>, UpdatePersonal>({
			query: (body) => ({
				url: `/web/family-member/${body.id}/personal-information`,
				method: 'PUT',
				body,
			}),
		}),
		updateEducation: builder.mutation<SuccessResponse<any>, EditEducation>({
			query: (body) => ({
				url: `/web/family-member/${body.id}/occupation-education`,
				method: 'PUT',
				body,
			}),
		}),
		updateRemarks: builder.mutation<
			SuccessResponse<any>,
			{
				pirRemarks?: boolean;
				applicantRemarks?: boolean;
				id: string;
			}
		>({
			query: (body) => ({
				url: `/web/family-member/${body.id}/internal-remarks`,
				method: 'PUT',
				body,
			}),
		}),
		deleteFamily: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/family-member/${id}`,
				method: 'DELETE',
			}),
		}),
		closeAllMembership: builder.mutation<
			SuccessResponse<any>,
			{
				dateOfExitService: string;
				id: string;
			}
		>({
			query: (body) => ({
				url: `/web/family-member/${body.id}/close-all-membership`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['FamilyDetail', 'FamilyFormList'],
		}),
		queryFamilyGroupList: builder.query<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/family-member/${id}/family-member-group`,
				method: 'GET',
			}),

			providesTags: ['FamilyFormList'],
		}),
		getFormsFamilyList: builder.query<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/form/family-member-application/list/${id}`,
				method: 'GET',
			}),
			providesTags: ['FamilyFormFormList'],
		}),
		changeFamilyNextFileNo: builder.mutation<
			SuccessResponse<any>,
			{ fiscalYear: string }
		>({
			query: (body) => ({
				url: `/web/family-member/generator-family-member-file-no`,
				method: 'POST',
				body,
			}),
		}),
		addFamilyMemberToList: builder.mutation<
			SuccessResponse<any>,
			RelationshipSaveDTO
		>({
			query: (body) => ({
				url: `/web/family-member/create-relationship`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['FamilyFormList'],
		}),
		// CHA-006 §4.1: every pair that needs a relationship when B joins A's family (groups merge).
		getFamilyGroupMergeCombination: builder.mutation<
			SuccessResponse<FamilyMemberRelationshipDTO[]>,
			{ memberId: string; targetMemberId: string }
		>({
			query: ({ memberId, targetMemberId }) => ({
				url: `/web/family-member/${memberId}/family-member-group/merge-combination/${targetMemberId}`,
				method: 'GET',
			}),
		}),
		// CHA-006 §三/§4.2: saves every pair's two-way relationship and merges the groups.
		mergeFamilyRelationship: builder.mutation<
			SuccessResponse<null>,
			FamilyMemberRelationshipMergeSaveDTO
		>({
			query: (body) => ({
				url: `/web/family-member/merge-relationship`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['FamilyFormList'],
		}),
		deleteFamilyMemberList: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/family-member/relationship/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['FamilyFormList'],
		}),
		getFamilyFormDetail: builder.query<
			SuccessResponse<FamilyMemberApplicationDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/form/family-member-application/${id}`,
				method: 'GET',
			}),
		}),
		deleteFamilyMemberForm: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/form/family-member-application/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['FamilyFormFormList'],
		}),
	}),
});

export const {
	useGetFamilyListQuery,
	useCreateFamilyMutation,
	useCheckFamilyMemberDuplicationMutation,
	useCheckClosedFamilyMemberDuplicationMutation,
	useCheckFamilyMemberMemberDuplicationMutation,
	useGetFamilyMemberFileNoHistoryQuery,
	useGetFamilyNextFileNoQuery,
	useGetFamilyToCreateQuery,
	useGetFamilyGroupListMutation,
	useGetFamilyDetailQuery,
	useUpdateServiceDetailsMutation,
	useUpdatePersonalMutation,
	useUpdateEducationMutation,
	useUpdateRemarksMutation,
	useDeleteFamilyMutation,
	useCloseAllMembershipMutation,
	useQueryFamilyGroupListQuery,
	useGetFormsFamilyListQuery,
	useChangeFamilyNextFileNoMutation,
	useAddFamilyMemberToListMutation,
	useGetFamilyGroupMergeCombinationMutation,
	useMergeFamilyRelationshipMutation,
	useDeleteFamilyMemberListMutation,
	useGetFamilyFormDetailQuery,
	useDeleteFamilyMemberFormMutation,
} = familyApi;
